import { chain, flatMap } from 'lodash';
import { V4 } from '@moleculeone/utils';

import { removeAtom, removeBond } from './methods';
import { neighborsList } from './selectors';

export function emptyMol() {
  return {
    nextAtom: 0,
    nextBond: 0,
    atoms: {},
    bonds: {},
    positions: {},
  };
}

export function removeAtomOrBond(mol, atom, bond, atomsList = []) {
  const bondsList = [];

  if (atomsList.length === 0) {
    if (bond != null) {
      bondsList.push(bond.idx);

      if (neighborsList(mol, bond.start).length === 1) {
        atomsList.push(bond.start);
      }

      if (neighborsList(mol, bond.end).length === 1) {
        atomsList.push(bond.end);
      }
    }

    if (atom != null) {
      atomsList.push(atom.idx);

      for (const [, natom] of neighborsList(mol, atom.idx)) {
        if (neighborsList(mol, natom.idx).length === 1) {
          atomsList.push(natom.idx);
        }
      }
    }
  }

  let newMol = mol;

  for (const idx of new Set(bondsList)) {
    newMol = removeBond(newMol, idx);
  }

  for (const idx of new Set(atomsList)) {
    newMol = removeAtom(newMol, idx);
  }

  return newMol;
}

export function moveMolsByVector(mols, targetVector = V4.zerovect) {
  return mols.map(mol => ({
    ...mol,
    positions: Object.entries(mol.positions).reduce((acc, [idx, position]) => {
      return {
        ...acc,
        [idx]: V4.add(position, targetVector),
      };
    }, {}),
  }));
}

export function moveDrawnMolsByVector(drawnMols, targetVector = V4.zerovect) {
  return drawnMols.map(drawnMol => ({
    ...drawnMol,
    atoms: Object.entries(drawnMol.atoms).reduce((acc, [idx, atom]) => {
      return {
        ...acc,
        [idx]: {
          ...atom,
          pos: V4.add(atom.pos, targetVector),
        },
      };
    }, {}),
    bbox: {
      min: V4.add(drawnMol.bbox.min, targetVector),
      max: V4.add(drawnMol.bbox.max, targetVector),
      size: drawnMol.bbox.size,
    },
  }));
}

export function moveMolsToPosition(mols, targetPosition) {
  const positions = [...flatMap(mols, mol => Object.values(mol.positions))];
  const vector = V4.average(V4.max(...positions), V4.min(...positions));

  return moveMolsByVector(mols, V4.sub(targetPosition, vector));
}

export function moveDrawnMolsToPosition(drawnMols, targetPosition) {
  const positions = [...flatMap(drawnMols, drawnMol => Object.values(drawnMol.atoms).map(atom => atom.pos))];
  const vector = V4.average(V4.max(...positions), V4.min(...positions));

  return moveDrawnMolsByVector(drawnMols, V4.sub(targetPosition, vector));
}

export function moveMolsToZero(mols) {
  return moveMolsToPosition(mols, V4.zerovect);
}

export function moveDrawnMolsToZero(drawnMols) {
  return moveDrawnMolsToPosition(drawnMols, V4.zerovect);
}

export function getBoundingPoints(drawnMols, border = V4.zerovect) {
  return chain(drawnMols)
    .flatMap(drawnMol => [drawnMol.bbox.min, drawnMol.bbox.max])
    .thru(points => ({
      min: V4.sub(V4.min(...points), border),
      max: V4.add(V4.max(...points), border),
    }))
    .value();
}

export function getBoundingBox(drawnMols, border = V4.zerovect) {
  const { min, max } = getBoundingPoints(drawnMols, border);
  const [width, height] = V4.sub(max, min);

  return {
    x: min[0],
    y: min[1],
    width,
    height,
  };
}
